// frontend-crm/src/pages/Login.js
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Card, CardContent, TextField, Button, Typography,
  Alert, CircularProgress, InputAdornment, IconButton, useTheme
} from '@mui/material';
import {
  Email as EmailIcon,
  Lock as LockIcon,
  Visibility,
  VisibilityOff,
} from '@mui/icons-material';

const API_URL = process.env.REACT_APP_API_URL || '';

const darkInputSx = (theme) => ({
  '& .MuiOutlinedInput-root': {
    bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)',
    color: theme.palette.text.primary,
    '& fieldset': { borderColor: theme.palette.divider },
    '&:hover fieldset': { borderColor: theme.palette.primary.main },
    '&.Mui-focused fieldset': { borderColor: theme.palette.primary.main },
  },
  '& .MuiInputLabel-root': { color: theme.palette.text.secondary },
  '& .MuiInputLabel-root.Mui-focused': { color: theme.palette.primary.main },
});

export default function Login() {
  const navigate = useNavigate();
  const theme = useTheme();
  const [form, setForm] = useState({ email: '', password: '' });
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      const data = await res.json();

      if (!res.ok || !data.token) {
        setError(data.error || 'Credenciales incorrectas');
        return;
      }

      localStorage.setItem('token', data.token);
      localStorage.setItem('user', JSON.stringify(data.user));

      if (data.user?.tipo === 'superadmin') {
        navigate('/superadmin');
      } else {
        navigate('/');
      }
    } catch (err) {
      setError('Error de conexión con el servidor');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: theme.palette.background.default,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Background decorations */}
      <Box sx={{
        position: 'absolute', top: -100, left: -100,
        width: 400, height: 400, borderRadius: '50%',
        background: `radial-gradient(circle, ${theme.palette.primary.main}26, transparent 70%)`,
        pointerEvents: 'none',
      }} />
      <Box sx={{
        position: 'absolute', bottom: -100, right: -100,
        width: 400, height: 400, borderRadius: '50%',
        background: `radial-gradient(circle, ${theme.palette.secondary.main}1A, transparent 70%)`,
        pointerEvents: 'none',
      }} />

      <Card
        sx={{
          bgcolor: theme.palette.background.paper,
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 4,
          width: '100%',
          maxWidth: 420,
          mx: 2,
          boxShadow: theme.palette.mode === 'dark' ? '0 24px 80px rgba(0,0,0,0.6)' : '0 12px 40px rgba(0,0,0,0.1)',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Top gradient line */}
        <Box sx={{ height: 3, background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`, borderRadius: '4px 4px 0 0' }} />

        <CardContent sx={{ p: 4 }}>
          {/* Logo / Icon */}
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Box
              sx={{
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                width: 64, height: 64, borderRadius: 3,
                background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                mb: 2, boxShadow: `0 8px 32px ${theme.palette.primary.main}66`,
              }}
            >
              <Typography fontSize={28}>🚀</Typography>
            </Box>
            <Typography variant="h5" fontWeight={700} sx={{ color: theme.palette.text.primary }}>
              CRM Mensajería
            </Typography>
            <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mt: 0.5 }}>
              Ingresa con tu cuenta
            </Typography>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 3, bgcolor: 'rgba(255,107,107,0.1)', color: '#FF6B6B', border: '1px solid rgba(255,107,107,0.2)' }}>
              {error}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            <TextField
              fullWidth label="Correo Electrónico" type="email"
              value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
              required autoFocus
              InputProps={{ startAdornment: <InputAdornment position="start"><EmailIcon sx={{ color: theme.palette.text.secondary, fontSize: 18 }} /></InputAdornment> }}
              sx={darkInputSx(theme)}
            />
            <TextField
              fullWidth label="Contraseña" type={showPass ? 'text' : 'password'}
              value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
              required
              InputProps={{
                startAdornment: <InputAdornment position="start"><LockIcon sx={{ color: theme.palette.text.secondary, fontSize: 18 }} /></InputAdornment>,
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowPass(!showPass)} size="small" sx={{ color: theme.palette.text.secondary }}>
                      {showPass ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                    </IconButton>
                  </InputAdornment>
                )
              }}
              sx={darkInputSx(theme)}
            />

            <Button
              type="submit"
              variant="contained"
              fullWidth
              disabled={loading}
              sx={{
                mt: 1, py: 1.5,
                background: theme.palette.primary.main,
                color: theme.palette.primary.contrastText,
                fontSize: '1rem', fontWeight: 600,
                boxShadow: `0 8px 24px ${theme.palette.primary.main}66`,
                '&:hover': { background: theme.palette.primary.dark, boxShadow: `0 12px 32px ${theme.palette.primary.main}99` },
                '&:disabled': { opacity: 0.6 },
              }}
            >
              {loading ? <CircularProgress size={22} sx={{ color: '#fff' }} /> : 'Iniciar Sesión'}
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
}
